import { forwardRef, useRef, type TextareaHTMLAttributes } from "react";
import { useAutoGrow } from "@/shared/hooks/useAutoGrow";

type AutoGrowTextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value"> & {
    value: string;
    /**
     * For copy that has to stay on one line (a headline, a CTA). It still wraps so all of it
     * shows, but Enter and pasted line breaks never add a line, same as the input it replaces.
     */
    singleLine?: boolean;
};

/**
 * A textarea that is always as tall as its content (see useAutoGrow).
 *
 * The ref is forwarded so a caller can reach the real textarea -- the ad token picker
 * (MPN-1932) needs `selectionStart` and `setSelectionRange` to insert at the caret.
 * Forwarding is additive: the internal ref that drives the autogrow is still set on every
 * render, so a caller that passes no ref behaves exactly as before.
 */
const AutoGrowTextarea = forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>(function AutoGrowTextarea({
    value,
    singleLine = false,
    rows = 1,
    style,
    onChange,
    onKeyDown,
    ...rest
}, forwardedRef) {
    const ref = useRef<HTMLTextAreaElement | null>(null);
    useAutoGrow(ref, value);

    return (
        <textarea
            {...rest}
            ref={(node) => {
                ref.current = node;
                if (typeof forwardedRef === "function") forwardedRef(node);
                else if (forwardedRef) forwardedRef.current = node;
            }}
            value={value}
            rows={rows}
            style={{ resize: "none", display: "block", ...style }}
            onKeyDown={(e) => {
                if (singleLine && e.key === "Enter") e.preventDefault();
                onKeyDown?.(e);
            }}
            onChange={(e) => {
                if (singleLine && /[\r\n]/.test(e.target.value)) {
                    e.target.value = e.target.value.replace(/\r?\n|\r/g, " ");
                }
                onChange?.(e);
            }}
        />
    );
});

export default AutoGrowTextarea;
